import { type FC } from 'react';
import { useSelector } from 'react-redux';

import { ChangesStatus } from '@editor2/types/changesStatus';
import { type RootState } from '@editor2/reducers/root';
import { pageToPageProperties } from '@editor2/mappers/page';
import ChangesHeader from './common/ChangesHeader';
import AttributesChanges from './common/AttributesChanges';
import ChildrenChanges from './common/ChildrenChanges';

interface Props {
	courseId: number;
	pageId: string;
}

const PageChanges: FC<Props> = (props) => {
	const { pageId, courseId } = props;

	const { changes, pages, page_groups, chapters, initialCourse } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const changesStatuses = changes.pages[pageId];
	const currentPage = pages.entities[pageId];
	const initialPage = initialCourse.pages.entities[pageId];

	const getPageNumber = (pageId: string) => {
		const initPage = initialCourse.pages.entities[pageId];
		const page = pages.entities[pageId];

		const deleted = changes.pages[pageId].includes(ChangesStatus.Deleted);

		switch (true) {
			case page && page.page_number && page.page_number !== ' ':
				return page.page_number;
			case deleted && initPage.page_number && initPage.page_number !== ' ':
				return initPage.page_number;
			default: {
				const entity = page || initPage;
				return entity.name;
			}
		}
	};

	const getPageGroupByPageId = (pageId: string) => {
		return Object.keys(page_groups.entities).find((key) =>
			page_groups.entities[key].pages.includes(pageId)
		);
	};

	const getChapterByPageId = (pageId: string) => {
		const pageGroupId = getPageGroupByPageId(pageId);

		if (pageGroupId) {
			return Object.keys(chapters.entities).find((key) =>
				chapters.entities[key].page_groups?.includes(pageGroupId)
			);
		} else {
			return Object.keys(chapters.entities).find((key) =>
				chapters.entities[key].pages?.includes(pageId)
			);
		}
	};

	const getPageLink = () => {
		const pageGroupId = getPageGroupByPageId(pageId);
		const chapterId = getChapterByPageId(pageId);

		return (
			`/courses/${courseId}/chapters/${chapterId}` +
			(pageGroupId ? `/page_groups/${pageGroupId}` : '') +
			`/pages/${pageId}`
		);
	};

	return (
		<div className="changeBlock">
			<ChangesHeader
				changesStatuses={changesStatuses}
				headerTitle={`Page ${getPageNumber(pageId)}`}
				link={changesStatuses.includes(ChangesStatus.Deleted) ? null : getPageLink()}
			/>

			{changesStatuses.includes(ChangesStatus.Modified) &&
				!changesStatuses.includes(ChangesStatus.New) && (
					<AttributesChanges
						currentEntity={currentPage}
						initialEntity={initialPage}
						mapper={pageToPageProperties}
					/>
				)}

			{changesStatuses.includes(ChangesStatus.ChildrenReorder) && (
				<ChildrenChanges
					currentIds={currentPage.elements}
					initialIds={initialPage?.elements || []}
					changesEntity={changes.elements}
					title="element"
				/>
			)}
		</div>
	);
};

export default PageChanges;
